import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  TextField,
  MenuItem,
  Select,
  makeStyles,
} from "@material-ui/core";
import { CRON_PRESETS, buildDailyCron, detectPreset } from "../../Helpers/cronHelpers";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  scheduleSelect: {
    minWidth: 160,
  },
  hourSelect: {
    minWidth: 120,
  },
  cronInput: {
    maxWidth: 200,
  },
}));

interface SchedulePickerProps {
  value: string;
  onChange: (cron: string) => void;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i >= 12 ? "PM" : "AM";
  const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: i, label: `${displayHour}:00 ${ampm}` };
});

export default function SchedulePicker({ value, onChange }: SchedulePickerProps) {
  const classes = useStyles();
  const preset = detectPreset(value);

  const [selectedType, setSelectedType] = useState(preset.type);
  const [dailyHour, setDailyHour] = useState(preset.hour ?? 2);
  const [customCron, setCustomCron] = useState(preset.type === "custom" ? value : "");

  useEffect(() => {
    const newPreset = detectPreset(value);
    setSelectedType(newPreset.type);
    if (newPreset.type === "daily" && newPreset.hour !== undefined) {
      setDailyHour(newPreset.hour);
    }
    if (newPreset.type === "custom") {
      setCustomCron(value);
    }
  }, [value]);

  const handleTypeChange = (type: string) => {
    setSelectedType(type as typeof selectedType);
    if (type === "every6h") onChange("0 */6 * * *");
    else if (type === "every12h") onChange("0 */12 * * *");
    else if (type === "daily") onChange(buildDailyCron(dailyHour));
    else if (type === "custom") onChange(customCron || value);
  };

  const handleHourChange = (hour: number) => {
    setDailyHour(hour);
    onChange(buildDailyCron(hour));
  };

  const handleCustomChange = (cron: string) => {
    setCustomCron(cron);
    onChange(cron);
  };

  return (
    <div className={classes.root}>
      <FormControl variant="outlined" size="small" className={classes.scheduleSelect}>
        <InputLabel>Schedule</InputLabel>
        <Select
          value={selectedType}
          onChange={(e) => handleTypeChange(e.target.value as string)}
          label="Schedule"
        >
          {CRON_PRESETS.map((preset) => (
            <MenuItem key={preset.value} value={preset.value}>
              {preset.label}
            </MenuItem>
          ))}
          <MenuItem value="daily">Daily at...</MenuItem>
          <MenuItem value="custom">Custom cron</MenuItem>
        </Select>
      </FormControl>
      {selectedType === "daily" && (
        <FormControl variant="outlined" size="small" className={classes.hourSelect}>
          <InputLabel>Time</InputLabel>
          <Select
            value={dailyHour}
            onChange={(e) => handleHourChange(e.target.value as number)}
            label="Time"
          >
            {HOUR_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {selectedType === "custom" && (
        <TextField
          value={customCron}
          onChange={(e) => handleCustomChange(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="0 */6 * * *"
          className={classes.cronInput}
        />
      )}
    </div>
  );
}
